export interface PowerField {
  id: string;
  label: string;
}

export const POWER_FIELDS: PowerField[] = [
  {id: 'kP1', label: 'Circulation Pump'},
  {id: 'kP1H', label: 'Pump 1'},
  {id: 'kP2', label: 'Pump 2'},
  {id: 'kP3', label: 'Pump 3'},
  {id: 'kSD', label: 'SDS'},
  {id: 'kYe', label: 'YESS'},
  {id: 'kBL1', label: 'Blower 1'},
  {id: 'kBL2', label: 'Blower 2'},
  {id: 'kH1', label: 'Heater'},
  {id: 'kH2', label: 'Heater 2'},
];

export interface DateRangeOption {
  value: string;
  label: string;
  ms: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  {value: 'last_day', label: 'Last Day', ms: DAY_MS},
  {value: 'last_7_days', label: 'Last 7 Days', ms: 7 * DAY_MS},
  {value: 'last_30_days', label: 'Last 30 Days', ms: 30 * DAY_MS},
];

export interface TimezoneOption {
  value: string;
  name: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  {value: 'Etc/GMT+12',                   name: 'International Date Line West'},
  {value: 'Pacific/Pago_Pago',            name: 'Samoa Time'},
  {value: 'Pacific/Honolulu',             name: 'Hawaii-Aleutian Time'},
  {value: 'America/Anchorage',            name: 'Alaska Time'},
  {value: 'America/Los_Angeles',          name: 'Pacific Time'},
  {value: 'America/Denver',               name: 'Mountain Time'},
  {value: 'America/Chicago',              name: 'Central Time'},
  {value: 'America/New_York',             name: 'Eastern Time'},
  {value: 'America/Halifax',              name: 'Atlantic Time'},
  {value: 'America/Argentina/Buenos_Aires', name: 'Argentina Time'},
  {value: 'Atlantic/South_Georgia',       name: 'Mid-Atlantic Time'},
  {value: 'Atlantic/Azores',              name: 'Azores Time'},
  {value: 'Etc/UTC',                      name: 'Coordinated Universal Time'},
  {value: 'Europe/Paris',                 name: 'Central European Time'},
  {value: 'Europe/Athens',                name: 'Eastern European Time'},
  {value: 'Europe/Moscow',                name: 'Moscow Time'},
  {value: 'Asia/Dubai',                   name: 'Gulf Standard Time'},
  {value: 'Asia/Karachi',                 name: 'Pakistan Time'},
  {value: 'Asia/Kolkata',                 name: 'India Standard Time'},
  {value: 'Asia/Dhaka',                   name: 'Bangladesh Time'},
  {value: 'Asia/Bangkok',                 name: 'Indochina Time'},
  {value: 'Asia/Shanghai',                name: 'China Standard Time'},
  {value: 'Asia/Tokyo',                   name: 'Japan Standard Time'},
  {value: 'Australia/Sydney',             name: 'Eastern Australia Time'},
  {value: 'Pacific/Guadalcanal',          name: 'Solomon Islands Time'},
  {value: 'Pacific/Auckland',             name: 'New Zealand Time'},
  {value: 'Pacific/Tongatapu',            name: 'Tonga Time'},
  {value: 'Pacific/Kiritimati',           name: 'Line Islands Time'},
];

export const getOffsetHours = (iana: string, at: Date = new Date()): number => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {timeZone: iana, timeZoneName: 'longOffset'}).formatToParts(at);
    const offsetStr = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    const match = offsetStr.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/i);
    if (!match) return 0;
    const sign = match[1] === '-' ? -1 : 1;
    const h = parseInt(match[2], 10);
    const m = match[3] ? parseInt(match[3], 10) : 0;
    return sign * (h + m / 60);
  } catch {
    return 0;
  }
};

export const formatOffset = (hours: number): string => {
  const sign = hours >= 0 ? '+' : '-';
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${String(m).padStart(2, '0')}`;
};

export const formatTimezoneLabel = (opt: TimezoneOption, at: Date = new Date()): string =>
  `${opt.name} (${formatOffset(getOffsetHours(opt.value, at))})`;

export const CURRENCY_OPTIONS: string[] = [
  'AUD', 'CAD', 'CZK', 'DKK', 'EUR', 'GBP', 'ISK', 'NOK', 'RUB', 'SEK', 'USD',
];

// ── Currency guess ──────────────────────────────────────────────────────────
// The spa doesn't record a country, so the starting currency comes from the
// viewer's browser: timezone first (closest to location, and it tells Canada
// from the US even on an en-US browser), then the language's region, then CAD.
// A currency the user picks is remembered in this browser.

const CURRENCY_STORAGE_KEY = 'power-currency';

const EURO_REGIONS = ['AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK'];

const REGION_CURRENCY: Record<string, string> = {
  AU: 'AUD', CA: 'CAD', CZ: 'CZK', DK: 'DKK', GB: 'GBP', IS: 'ISK', NO: 'NOK', RU: 'RUB', SE: 'SEK', US: 'USD',
  ...Object.fromEntries(EURO_REGIONS.map(r => [r, 'EUR'])),
};

const CANADIAN_ZONES = [
  'Toronto', 'Montreal', 'Vancouver', 'Edmonton', 'Calgary', 'Winnipeg', 'Regina', 'Halifax',
  'St_Johns', 'Moncton', 'Glace_Bay', 'Goose_Bay', 'Whitehorse', 'Yellowknife', 'Iqaluit',
  'Swift_Current', 'Dawson_Creek', 'Fort_Nelson', 'Creston', 'Rankin_Inlet', 'Cambridge_Bay',
];

const ZONE_CURRENCY: Record<string, string> = {
  'Europe/London': 'GBP', 'Europe/Copenhagen': 'DKK', 'Europe/Oslo': 'NOK', 'Europe/Stockholm': 'SEK',
  'Europe/Prague': 'CZK', 'Europe/Moscow': 'RUB', 'Atlantic/Reykjavik': 'ISK',
};

const currencyFromTimezone = (): string | null => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    if (ZONE_CURRENCY[zone]) return ZONE_CURRENCY[zone];
    const [area, city] = zone.split('/');
    if (area === 'America') return CANADIAN_ZONES.includes(city) ? 'CAD' : null; // US vs others: leave to the locale
    if (area === 'Canada') return 'CAD';
    if (area === 'Australia') return 'AUD';
    if (area === 'Europe') return 'EUR';
    return null;
  } catch {
    return null;
  }
};

const currencyFromLocale = (): string | null => {
  const langs = typeof navigator !== 'undefined' ? (navigator.languages?.length ? navigator.languages : [navigator.language]) : [];
  for (const lang of langs) {
    const region = /[-_]([A-Za-z]{2})\b/.exec(lang ?? '')?.[1]?.toUpperCase();
    if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
  }
  return null;
};

/** The saved currency, else a guess from the browser's location settings. */
export const initialCurrency = (): string => {
  try {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (saved && CURRENCY_OPTIONS.includes(saved)) return saved;
  } catch {
    // Storage unavailable: fall through to the guess.
  }
  const guess = currencyFromTimezone() ?? currencyFromLocale();
  return guess && CURRENCY_OPTIONS.includes(guess) ? guess : 'CAD';
};

export const saveCurrency = (currency: string) => {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  } catch {
    // Storage unavailable: the choice just won't persist.
  }
};
